import React, { useMemo } from 'react';
import EmptyState from './EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface MonthRow {
  year: number;
  month: number;
  pnl: number;
  pctReturn: number | null;
}

/** Compact Indian notation: Cr / L / K. Mirrors PerformanceReport's
 *  marketplace heatmap so users see consistent number formatting across
 *  pages. */
function fmtCompact(n: number): string {
  const sign = n >= 0 ? '+' : '-';
  const abs = Math.abs(n);
  if (abs >= 1_00_00_000) return `${sign}${(abs / 1_00_00_000).toFixed(1)}Cr`;
  if (abs >= 1_00_000)    return `${sign}${(abs / 1_00_000).toFixed(1)}L`;
  if (abs >= 1_000)       return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${abs}`;
}

function fmtPct(p: number | null): string {
  if (p === null || p === undefined) return '—';
  return `${p >= 0 ? '+' : ''}${p.toFixed(1)}%`;
}

const HeatmapTable: React.FC<{ months: MonthRow[] }> = ({ months }) => {
  const years = useMemo(() => {
    const map = new Map<number, Map<number, MonthRow>>();
    for (const r of months) {
      if (!map.has(r.year)) map.set(r.year, new Map());
      map.get(r.year)!.set(r.month, r);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a - b);
  }, [months]);

  const maxAbsPnl = useMemo(
    () => Math.max(...months.map(r => Math.abs(r.pnl)), 1),
    [months],
  );

  // Background intensity scales with |pnl| relative to the max in range.
  // Green for gains, red for losses.
  const cellStyle = (pnl: number | undefined): React.CSSProperties => {
    if (pnl === undefined) return {};
    const intensity = Math.min(Math.abs(pnl) / maxAbsPnl, 1);
    const opacity = 0.15 + intensity * 0.55;
    return pnl >= 0
      ? { backgroundColor: `rgba(16, 185, 129, ${opacity})` }
      : { backgroundColor: `rgba(239, 68, 68, ${opacity})` };
  };

  if (!months.length) return <EmptyState message="No monthly data in this range." />;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse" style={{ fontSize: '0.75rem' }}>
        <thead>
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-14">
              Year
            </th>
            {MONTHS.map(m => (
              <th
                key={m}
                className="px-1 py-2 text-center font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                style={{ letterSpacing: '0.05em', minWidth: 64 }}
              >
                {m}
              </th>
            ))}
            <th
              className="px-2 py-2 text-center font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-l border-gray-200 dark:border-gray-700"
              style={{ minWidth: 80 }}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {years.map(([year, monthMap]) => {
            const rows = Array.from(monthMap.values());
            const yearTotal = rows.reduce((s, r) => s + r.pnl, 0);
            const yearPct = rows.reduce(
              (s, r) => s + (r.pctReturn ?? 0),
              0,
            );
            return (
              <tr key={year} className="border-t border-gray-100 dark:border-gray-700/50">
                <td className="px-3 py-1 text-sm font-semibold text-gray-900 dark:text-white">{year}</td>
                {MONTHS.map((_, idx) => {
                  const r = monthMap.get(idx + 1);
                  if (!r) {
                    return (
                      <td key={idx} className="px-1 py-1">
                        <div className="h-14 rounded-lg flex items-center justify-center bg-gray-100/30 dark:bg-gray-700/30">
                          <span className="text-gray-300 dark:text-gray-600" style={{ fontSize: '0.6rem' }}>—</span>
                        </div>
                      </td>
                    );
                  }
                  const pos = r.pnl >= 0;
                  return (
                    <td key={idx} className="px-1 py-1">
                      <div
                        className="h-14 rounded-lg flex flex-col items-center justify-center gap-0.5 transition-transform duration-200 hover:scale-[1.04] hover:shadow-md cursor-default"
                        style={{ ...cellStyle(r.pnl), fontVariantNumeric: 'tabular-nums' }}
                        title={`${MONTHS[idx]} ${year} • ${fmtCompact(r.pnl)}${r.pctReturn !== null ? ` • ${fmtPct(r.pctReturn)}` : ''}`}
                      >
                        <span
                          className={`font-bold leading-none ${pos ? 'text-emerald-800 dark:text-emerald-100' : 'text-red-800 dark:text-red-100'}`}
                          style={{ fontSize: '0.7rem' }}
                        >
                          {fmtCompact(r.pnl)}
                        </span>
                        <span
                          className={`leading-none opacity-80 ${pos ? 'text-emerald-700 dark:text-emerald-200' : 'text-red-700 dark:text-red-200'}`}
                          style={{ fontSize: '0.55rem' }}
                        >
                          {fmtPct(r.pctReturn)}
                        </span>
                      </div>
                    </td>
                  );
                })}
                <td className="px-1 py-1 border-l border-gray-200 dark:border-gray-700">
                  <div
                    className="h-14 rounded-lg flex flex-col items-center justify-center gap-0.5 bg-gray-50 dark:bg-gray-700/50 transition-transform duration-200 hover:scale-[1.04] cursor-default"
                    style={{ ...cellStyle(yearTotal), fontVariantNumeric: 'tabular-nums' }}
                  >
                    <span
                      className={`font-bold leading-none ${yearTotal >= 0 ? 'text-emerald-800 dark:text-emerald-100' : 'text-red-800 dark:text-red-100'}`}
                      style={{ fontSize: '0.7rem' }}
                    >
                      {fmtCompact(yearTotal)}
                    </span>
                    <span
                      className={`leading-none opacity-80 ${yearTotal >= 0 ? 'text-emerald-700 dark:text-emerald-200' : 'text-red-700 dark:text-red-200'}`}
                      style={{ fontSize: '0.55rem' }}
                    >
                      {fmtPct(Math.round(yearPct * 10) / 10)}
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/** Year × Month P&L heatmap card. Backed by `summary.monthlyReturns` from
 *  /analytics/summary — no extra fetch needed. Shows on the Overview section
 *  and on the basket / strategy drilldown so users can spot seasonality
 *  in both the global view and a narrowed scope. */
const MonthlyHeatmap: React.FC<{ months: MonthRow[]; title?: string }> = ({
  months,
  title = 'Monthly Returns',
}) => (
  <Card>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <HeatmapTable months={months} />
    </CardContent>
  </Card>
);

export default MonthlyHeatmap;
